import React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { ScrollArea, ScrollBar } from "../ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Button } from "../ui/button";
import { MultiSelect } from "../ui/multi-select";
import { GitCompare, ChevronDown, ChevronRight, ZoomIn, Maximize2, Minimize2, CheckCircle, XCircle, AlertTriangle } from "lucide-react";

const MESI_LABELS = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const MESI_FULL = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

const formatCurrency = (value) => {
  if (value === undefined || value === null) return "0 €";
  if (value === 0) return "0 €";
  if (Math.abs(value) >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M €`;
  }
  if (Math.abs(value) >= 1000) {
    return `${(value / 1000).toFixed(0)}k €`;
  }
  return `${value.toLocaleString("it-IT", { maximumFractionDigits: 0 })} €`;
};

const getStatoBadge = (varPerc) => {
  if (varPerc >= 5) return <CheckCircle className="h-4 w-4 text-green-500" />;
  if (varPerc <= -5) return <XCircle className="h-4 w-4 text-red-500" />;
  return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
};

export function BudgetConfrontoTable({
  categorie,
  budgetMatrix,
  consuntivoMatrix,
  totali,
  activeTab,
  expandedCategories,
  toggleCategory,
  centriCosto,
  filtroCdcIds,
  setFiltroCdcIds,
  zoomedMonth,
  setZoomedMonth,
  isTableExpanded,
  setIsTableExpanded
}) {
  const tipoPositivo = activeTab === "economico" ? "ricavo" : "entrata";

  // Helper per ottenere tutti i conti (merge budget + consuntivo)
  const getMergedConti = (catId) => {
    const budConti = budgetMatrix[catId]?.conti || {};
    const consConti = consuntivoMatrix[catId]?.conti || {};
    
    const mergedContiMap = { ...budConti };
    Object.keys(consConti).forEach(contoId => {
      if (!mergedContiMap[contoId]) {
        mergedContiMap[contoId] = {
          conto_id: contoId,
          conto_codice: consConti[contoId].conto_codice,
          conto_nome: consConti[contoId].conto_nome,
          mesi: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
          totale: 0
        };
      }
    });
    return Object.values(mergedContiMap);
  };

  const mesiToShow = zoomedMonth !== null ? [zoomedMonth] : MESI_LABELS.map((_, i) => i);
  const mesiLabels = zoomedMonth !== null ? [MESI_FULL[zoomedMonth]] : MESI_LABELS;
  const colWidth = zoomedMonth !== null ? "w-[150px]" : "w-[60px]";
  const tableMinWidth = zoomedMonth !== null ? "min-w-[800px]" : "min-w-[2000px]";

  const renderCategoryRows = (categorieFiltered, isPositive) => {
    return categorieFiltered.map(cat => (
      <React.Fragment key={cat.id}>
        <TableRow className={`${cat.colore} hover:bg-muted/30 cursor-pointer`} onClick={() => toggleCategory(cat.id)}>
          <TableCell className={`sticky left-0 ${cat.colore} z-10`}>
            <div className="flex items-center gap-2">
              {expandedCategories[cat.id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              <span className="text-sm">{cat.label}</span>
            </div>
          </TableCell>
          {mesiToShow.map((meseIdx, i) => {
            const budMese = budgetMatrix[cat.id]?.mesi[meseIdx] || 0;
            const consMese = consuntivoMatrix[cat.id]?.mesi[meseIdx] || 0;
            const varMese = consMese - budMese;
            const varPerc = budMese !== 0 ? (varMese / Math.abs(budMese)) * 100 : 0;
            return (
              <React.Fragment key={i}>
                <TableCell className={`text-center text-xs border-l ${colWidth}`}>{budMese > 0 ? formatCurrency(budMese) : "-"}</TableCell>
                <TableCell className={`text-center text-xs ${colWidth}`}>{consMese > 0 ? formatCurrency(consMese) : "-"}</TableCell>
                <TableCell className={`text-center text-xs ${colWidth} ${isPositive ? (varMese >= 0 ? 'text-green-600' : 'text-red-600') : (varMese <= 0 ? 'text-green-600' : 'text-red-600')}`}>
                  {(budMese > 0 || consMese > 0) ? formatCurrency(varMese) : "-"}
                </TableCell>
                <TableCell className="text-center">{(budMese > 0 || consMese > 0) && getStatoBadge(varPerc)}</TableCell>
              </React.Fragment>
            );
          })}
          {zoomedMonth === null && (() => {
            const budTot = budgetMatrix[cat.id]?.totale || 0;
            const consTot = consuntivoMatrix[cat.id]?.totale || 0;
            const varTot = consTot - budTot;
            const varPerc = budTot !== 0 ? (varTot / Math.abs(budTot)) * 100 : 0;
            return (
              <React.Fragment>
                <TableCell className="text-center text-xs border-l font-medium">{formatCurrency(budTot)}</TableCell>
                <TableCell className="text-center text-xs font-medium">{formatCurrency(consTot)}</TableCell>
                <TableCell className={`text-center text-xs font-medium ${isPositive ? (varTot >= 0 ? 'text-green-600' : 'text-red-600') : (varTot <= 0 ? 'text-green-600' : 'text-red-600')}`}>{formatCurrency(varTot)}</TableCell>
                <TableCell className="text-center">{getStatoBadge(varPerc)}</TableCell>
              </React.Fragment>
            );
          })()}
        </TableRow>
        
        {/* Dettaglio per conto */}
        {expandedCategories[cat.id] && getMergedConti(cat.id).map(conto => (
          <TableRow key={conto.conto_id} className="bg-white/50 text-xs">
            <TableCell className="sticky left-0 bg-white/50 z-10 pl-8">
              <span className="text-muted-foreground">{conto.conto_codice} {conto.conto_nome}</span>
            </TableCell>
            {mesiToShow.map((meseIdx, i) => {
              const budMese = conto.mesi[meseIdx] || 0;
              const consContoData = consuntivoMatrix[cat.id]?.conti[conto.conto_id];
              const consMese = consContoData?.mesi[meseIdx] || 0;
              const varMese = consMese - budMese;
              return (
                <React.Fragment key={i}>
                  <TableCell className={`text-center text-xs border-l ${colWidth}`}>{budMese > 0 ? formatCurrency(budMese) : "-"}</TableCell>
                  <TableCell className={`text-center text-xs ${colWidth}`}>{consMese > 0 ? formatCurrency(consMese) : "-"}</TableCell>
                  <TableCell className={`text-center text-xs ${colWidth} ${isPositive ? (varMese >= 0 ? 'text-green-600' : 'text-red-600') : (varMese <= 0 ? 'text-green-600' : 'text-red-600')}`}>
                    {(budMese > 0 || consMese > 0) ? formatCurrency(varMese) : "-"}
                  </TableCell>
                  <TableCell></TableCell>
                </React.Fragment>
              );
            })}
            {zoomedMonth === null && (
              <React.Fragment>
                <TableCell className="text-center text-xs border-l">{formatCurrency(conto.totale)}</TableCell>
                <TableCell className="text-center text-xs">{formatCurrency(consuntivoMatrix[cat.id]?.conti[conto.conto_id]?.totale || 0)}</TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
              </React.Fragment>
            )}
          </TableRow>
        ))}
      </React.Fragment>
    ));
  };

  return (
    <Card className={isTableExpanded ? "fixed inset-4 z-50 overflow-auto" : ""} data-testid="confronto-table">
      <CardHeader className="pb-2">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <GitCompare className="h-5 w-5" />
              Confronto Budget vs Consuntivo - {zoomedMonth !== null ? MESI_FULL[zoomedMonth] : "Vista Mensile"}
            </CardTitle>
            <CardDescription>Clicca su una categoria per vedere il dettaglio per conto</CardDescription>
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            <Select value={zoomedMonth !== null ? String(zoomedMonth) : "all"} onValueChange={(v) => setZoomedMonth(v === "all" ? null : parseInt(v))}>
              <SelectTrigger className="w-[160px]" data-testid="month-zoom-select">
                <ZoomIn className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Zoom mese" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i mesi</SelectItem>
                {MESI_FULL.map((m, i) => (
                  <SelectItem key={i} value={String(i)}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button variant="outline" size="sm" onClick={() => setIsTableExpanded(!isTableExpanded)} data-testid="expand-table-btn">
              {isTableExpanded ? <Minimize2 className="h-4 w-4 mr-1" /> : <Maximize2 className="h-4 w-4 mr-1" />}
              {isTableExpanded ? "Riduci" : "Espandi"}
            </Button>
            
            <MultiSelect
              options={centriCosto.map(c => ({ value: c.id, label: c.nome }))}
              selected={filtroCdcIds}
              onChange={setFiltroCdcIds}
              placeholder="Filtra per CDC..."
              searchPlaceholder="Cerca CDC..."
              className="w-[200px]"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <ScrollArea className={`w-full ${isTableExpanded ? 'h-[calc(100vh-200px)]' : ''}`}>
          <div className={tableMinWidth}>
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-[200px] sticky left-0 bg-muted/50 z-10">Categoria</TableHead>
                  {mesiLabels.map((m, i) => (
                    <React.Fragment key={i}>
                      <TableHead 
                        className={`text-center text-xs border-l cursor-pointer hover:bg-muted ${zoomedMonth === null ? '' : 'font-bold text-primary'}`}
                        colSpan={4}
                        onClick={() => zoomedMonth === null && setZoomedMonth(mesiToShow[i])}
                      >
                        {m}
                        {zoomedMonth === null && <ZoomIn className="h-3 w-3 inline ml-1 opacity-50" />}
                      </TableHead>
                    </React.Fragment>
                  ))}
                  {zoomedMonth === null && (
                    <TableHead className="text-center font-bold border-l" colSpan={4}>TOTALE</TableHead>
                  )}
                </TableRow>
                <TableRow className="bg-muted/30 text-xs">
                  <TableHead className="sticky left-0 bg-muted/30 z-10"></TableHead>
                  {[...mesiLabels, ...(zoomedMonth === null ? ["TOT"] : [])].map((_, i) => (
                    <React.Fragment key={i}>
                      <TableHead className={`text-center text-xs ${colWidth} ${i > 0 ? 'border-l' : ''}`}>Bud</TableHead>
                      <TableHead className={`text-center text-xs ${colWidth}`}>Cons</TableHead>
                      <TableHead className={`text-center text-xs ${colWidth}`}>Var</TableHead>
                      <TableHead className="text-center text-xs w-[40px]">St</TableHead>
                    </React.Fragment>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Sezione Ricavi/Entrate */}
                <TableRow className="bg-green-100/50 font-semibold">
                  <TableCell className="sticky left-0 bg-green-100/50 z-10">
                    {activeTab === "economico" ? "RICAVI" : "ENTRATE"}
                  </TableCell>
                  {mesiToShow.map((meseIdx, i) => {
                    const budMese = categorie.filter(c => c.tipo === tipoPositivo).reduce((s, c) => s + (budgetMatrix[c.id]?.mesi[meseIdx] || 0), 0);
                    const consMese = categorie.filter(c => c.tipo === tipoPositivo).reduce((s, c) => s + (consuntivoMatrix[c.id]?.mesi[meseIdx] || 0), 0);
                    const varMese = consMese - budMese;
                    const varPerc = budMese !== 0 ? (varMese / Math.abs(budMese)) * 100 : 0;
                    return (
                      <React.Fragment key={i}>
                        <TableCell className={`text-center text-xs border-l text-green-700 ${colWidth}`}>{formatCurrency(budMese)}</TableCell>
                        <TableCell className={`text-center text-xs text-green-700 ${colWidth}`}>{formatCurrency(consMese)}</TableCell>
                        <TableCell className={`text-center text-xs ${colWidth} ${varMese >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(varMese)}</TableCell>
                        <TableCell className="text-center">{getStatoBadge(varPerc)}</TableCell>
                      </React.Fragment>
                    );
                  })}
                  {zoomedMonth === null && (() => {
                    const budTot = categorie.filter(c => c.tipo === tipoPositivo).reduce((s, c) => s + (budgetMatrix[c.id]?.totale || 0), 0);
                    const consTot = categorie.filter(c => c.tipo === tipoPositivo).reduce((s, c) => s + (consuntivoMatrix[c.id]?.totale || 0), 0);
                    const varTot = consTot - budTot;
                    const varPerc = budTot !== 0 ? (varTot / Math.abs(budTot)) * 100 : 0;
                    return (
                      <React.Fragment>
                        <TableCell className="text-center text-xs border-l font-bold text-green-700">{formatCurrency(budTot)}</TableCell>
                        <TableCell className="text-center text-xs font-bold text-green-700">{formatCurrency(consTot)}</TableCell>
                        <TableCell className={`text-center text-xs font-bold ${varTot >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(varTot)}</TableCell>
                        <TableCell className="text-center">{getStatoBadge(varPerc)}</TableCell>
                      </React.Fragment>
                    );
                  })()}
                </TableRow>

                {renderCategoryRows(categorie.filter(c => c.tipo === tipoPositivo), true)}

                {/* Sezione Costi/Uscite */}
                <TableRow className="bg-red-100/50 font-semibold">
                  <TableCell className="sticky left-0 bg-red-100/50 z-10">
                    {activeTab === "economico" ? "COSTI" : "USCITE"}
                  </TableCell>
                  {mesiToShow.map((meseIdx, i) => {
                    const budMese = categorie.filter(c => c.tipo !== tipoPositivo).reduce((s, c) => s + (budgetMatrix[c.id]?.mesi[meseIdx] || 0), 0);
                    const consMese = categorie.filter(c => c.tipo !== tipoPositivo).reduce((s, c) => s + (consuntivoMatrix[c.id]?.mesi[meseIdx] || 0), 0);
                    const varMese = consMese - budMese;
                    const varPerc = budMese !== 0 ? (varMese / Math.abs(budMese)) * 100 : 0;
                    return (
                      <React.Fragment key={i}>
                        <TableCell className={`text-center text-xs border-l text-red-700 ${colWidth}`}>{formatCurrency(budMese)}</TableCell>
                        <TableCell className={`text-center text-xs text-red-700 ${colWidth}`}>{formatCurrency(consMese)}</TableCell>
                        <TableCell className={`text-center text-xs ${colWidth} ${varMese <= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(varMese)}</TableCell>
                        <TableCell className="text-center">{getStatoBadge(varPerc)}</TableCell>
                      </React.Fragment>
                    );
                  })}
                  {zoomedMonth === null && (() => {
                    const budTot = categorie.filter(c => c.tipo !== tipoPositivo).reduce((s, c) => s + (budgetMatrix[c.id]?.totale || 0), 0);
                    const consTot = categorie.filter(c => c.tipo !== tipoPositivo).reduce((s, c) => s + (consuntivoMatrix[c.id]?.totale || 0), 0);
                    const varTot = consTot - budTot;
                    const varPerc = budTot !== 0 ? (varTot / Math.abs(budTot)) * 100 : 0;
                    return (
                      <React.Fragment>
                        <TableCell className="text-center text-xs border-l font-bold text-red-700">{formatCurrency(budTot)}</TableCell>
                        <TableCell className="text-center text-xs font-bold text-red-700">{formatCurrency(consTot)}</TableCell>
                        <TableCell className={`text-center text-xs font-bold ${varTot <= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(varTot)}</TableCell>
                        <TableCell className="text-center">{getStatoBadge(varPerc)}</TableCell>
                      </React.Fragment>
                    );
                  })()}
                </TableRow>

                {renderCategoryRows(categorie.filter(c => c.tipo !== tipoPositivo), false)}

                {/* Riga Saldo */}
                <TableRow className={`font-bold ${totali.saldo >= 0 ? 'bg-green-200/50' : 'bg-red-200/50'}`}>
                  <TableCell className={`sticky left-0 ${totali.saldo >= 0 ? 'bg-green-200/50' : 'bg-red-200/50'} z-10`}>
                    {activeTab === "economico" ? "RISULTATO" : "SALDO CASSA"}
                  </TableCell>
                  {mesiToShow.map((meseIdx, i) => {
                    const m = totali.mensili[meseIdx];
                    const budMese = m.entrate - m.uscite;
                    const consMese = (categorie.filter(c => c.tipo === tipoPositivo).reduce((s, c) => s + (consuntivoMatrix[c.id]?.mesi[meseIdx] || 0), 0)) -
                                     (categorie.filter(c => c.tipo !== tipoPositivo).reduce((s, c) => s + (consuntivoMatrix[c.id]?.mesi[meseIdx] || 0), 0));
                    const varMese = consMese - budMese;
                    const varPerc = budMese !== 0 ? (varMese / Math.abs(budMese)) * 100 : 0;
                    return (
                      <React.Fragment key={i}>
                        <TableCell className={`text-center text-xs border-l ${colWidth} ${budMese >= 0 ? 'text-green-700' : 'text-red-700'}`}>{formatCurrency(budMese)}</TableCell>
                        <TableCell className={`text-center text-xs ${colWidth} ${consMese >= 0 ? 'text-green-700' : 'text-red-700'}`}>{formatCurrency(consMese)}</TableCell>
                        <TableCell className={`text-center text-xs ${colWidth} ${varMese >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(varMese)}</TableCell>
                        <TableCell className="text-center">{getStatoBadge(varPerc)}</TableCell>
                      </React.Fragment>
                    );
                  })}
                  {zoomedMonth === null && (() => {
                    const budTot = totali.saldo;
                    const consTot = (categorie.filter(c => c.tipo === tipoPositivo).reduce((s, c) => s + (consuntivoMatrix[c.id]?.totale || 0), 0)) -
                                    (categorie.filter(c => c.tipo !== tipoPositivo).reduce((s, c) => s + (consuntivoMatrix[c.id]?.totale || 0), 0));
                    const varTot = consTot - budTot;
                    const varPerc = budTot !== 0 ? (varTot / Math.abs(budTot)) * 100 : 0;
                    return (
                      <React.Fragment>
                        <TableCell className={`text-center text-xs border-l font-bold ${budTot >= 0 ? 'text-green-700' : 'text-red-700'}`}>{formatCurrency(budTot)}</TableCell>
                        <TableCell className={`text-center text-xs font-bold ${consTot >= 0 ? 'text-green-700' : 'text-red-700'}`}>{formatCurrency(consTot)}</TableCell>
                        <TableCell className={`text-center text-xs font-bold ${varTot >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(varTot)}</TableCell>
                        <TableCell className="text-center">{getStatoBadge(varPerc)}</TableCell>
                      </React.Fragment>
                    );
                  })()}
                </TableRow>
              </TableBody>
            </Table>
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
